/**
 * Frontend JavaScript for MR Blocks Popup
 * Complete implementation with all trigger types and features
 */

class PopupController {
	constructor(element) {
		this.popup = element;
		this.settings = this.parseAttributes();
		this.isVisible = false;
		this.isDismissed = this.checkDismissalStatus();
		this.scrollListener = null;
		this.exitIntentListener = null;
		this.keyListener = null;
		this.resizeListener = null;

		console.log("MR Popup Controller initialized:", {
			id: this.settings.id,
			trigger: this.settings.trigger,
			dismissed: this.isDismissed,
		});

		// Initialize if not dismissed
		if (!this.isDismissed) {
			this.init();
		} else {
			console.log("Popup dismissed, skipping initialization");
		}
	}

	/**
	 * Parse data attributes from popup element
	 */
	parseAttributes() {
		const dataset = this.popup.dataset;
		return {
			id: dataset.id || this.popup.id,
			trigger: dataset.trigger || "onLoad",
			wait: parseInt(dataset.wait) || 1,
			scroll: parseInt(dataset.scroll) || 50,
			showClose: dataset.showClose === "true",
			dismiss: dataset.dismiss === "true",
			dismissTime: parseInt(dataset.dismissTime) || 1,
			anchorId: dataset.anchorId || "",
			anchorClose: dataset.anchorClose || "",
			outsideClose: dataset.outsideClose !== "false",
			lockScrolling: dataset.lockScrolling !== "false",
			recurring: dataset.recurring === "true",
			recurringTime: parseInt(dataset.recurringTime) || 1,
			disableOn: dataset.disableOn || "none",
		};
	}

	/**
	 * Initialize popup based on trigger type
	 */
	init() {
		console.log("Initializing popup with trigger:", this.settings.trigger);

		// Check if disabled on current device
		if (this.isDisabledOnCurrentDevice()) {
			console.log("Popup disabled on current device:", this.settings.disableOn);
			return;
		}

		// Setup close button listeners
		this.setupCloseListeners();

		// Setup overlay click listener
		this.setupOverlayListener();

		// Setup keyboard listener
		this.setupKeyboardListener();

		// Initialize based on trigger type
		switch (this.settings.trigger) {
			case "onLoad":
				this.bindOnLoad();
				break;
			case "onScroll":
				this.bindOnScroll();
				break;
			case "onExit":
				this.bindExitIntent();
				break;
			case "onClick":
				this.bindAnchors();
				break;
		}
	}

	/**
	 * Check if popup should be disabled on current device
	 */
	isDisabledOnCurrentDevice() {
		const disableOn = this.settings.disableOn;
		const isMobile = window.innerWidth <= 768;
		const isTablet = window.innerWidth <= 1024 && window.innerWidth > 768;

		if (disableOn === "mobile" && isMobile) return true;
		if (disableOn === "tablet" && isTablet) return true;

		return false;
	}

	/**
	 * OnLoad trigger implementation
	 */
	bindOnLoad() {
		const delay = this.settings.wait * 1000; // Convert to milliseconds
		console.log(`Setting onLoad trigger with ${delay}ms delay`);

		setTimeout(() => {
			if (!this.isDismissed && !this.isVisible) {
				console.log("Triggering onLoad popup");
				this.showPopup();
			}
		}, delay);
	}

	/**
	 * OnScroll trigger implementation with debouncing
	 */
	bindOnScroll() {
		let ticking = false;
		console.log(
			`Setting onScroll trigger at ${this.settings.scroll}% threshold`,
		);

		this.scrollListener = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					const scrollPercent = this.calculateScrollPercent();
					if (scrollPercent >= this.settings.scroll && !this.isVisible) {
						console.log(`Scroll threshold reached: ${scrollPercent}%`);
						this.showPopup();
						// Remove listener after triggering
						window.removeEventListener("scroll", this.scrollListener);
					}
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", this.scrollListener, { passive: true });
	}

	/**
	 * Calculate scroll percentage
	 */
	calculateScrollPercent() {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollHeight = document.documentElement.scrollHeight;
		const clientHeight = document.documentElement.clientHeight;

		if (scrollHeight <= clientHeight) return 100; // Handle short pages

		return Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
	}

	/**
	 * Exit intent trigger implementation
	 */
	bindExitIntent() {
		let hasTriggered = false;
		console.log("Setting onExit intent trigger");

		this.exitIntentListener = (e) => {
			// Check if mouse is leaving from the top of the viewport
			if (e.clientY <= 0 && !hasTriggered && !this.isVisible) {
				hasTriggered = true;
				console.log("Exit intent detected");
				this.showPopup();

				// Remove listener after triggering
				document.removeEventListener("mouseleave", this.exitIntentListener);
			}
		};

		document.addEventListener("mouseleave", this.exitIntentListener);
	}

	/**
	 * OnClick anchor trigger implementation
	 */
	bindAnchors() {
		if (!this.settings.anchorId) {
			console.warn(
				"MR Blocks Popup: No anchor ID specified for onClick trigger",
			);
			return;
		}

		console.log(
			"Setting onClick triggers for anchors:",
			this.settings.anchorId,
		);

		// Support multiple anchors (comma-separated)
		const anchorIds = this.settings.anchorId.split(",").map((id) => id.trim());

		anchorIds.forEach((anchorId) => {
			// Use event delegation for dynamic content
			document.addEventListener("click", (e) => {
				const target = e.target.closest(
					`#${anchorId}, [data-popup-anchor="${anchorId}"]`,
				);
				if (target) {
					e.preventDefault();
					console.log("onClick trigger activated for anchor:", anchorId);
					this.showPopup();
				}
			});
		});

		// Handle close anchors
		if (this.settings.anchorClose) {
			const closeIds = this.settings.anchorClose
				.split(",")
				.map((id) => id.trim());
			closeIds.forEach((closeId) => {
				document.addEventListener("click", (e) => {
					const target = e.target.closest(
						`#${closeId}, [data-popup-close="${closeId}"]`,
					);
					if (target) {
						e.preventDefault();
						console.log("Close trigger activated for anchor:", closeId);
						this.hidePopup();
					}
				});
			});
		}
	}

	/**
	 * Setup close button listeners
	 */
	setupCloseListeners() {
		const closeButtons = this.popup.querySelectorAll(
			".otter-popup__modal_close",
		);
		console.log("Setting up close button listeners:", closeButtons.length);

		closeButtons.forEach((button) => {
			button.addEventListener("click", () => {
				console.log("Close button clicked");
				this.hidePopup();
			});
		});
	}

	/**
	 * Setup overlay click listener
	 */
	setupOverlayListener() {
		if (this.settings.outsideClose) {
			const overlay = this.popup.querySelector(
				".otter-popup__modal_wrap_overlay",
			);
			if (overlay) {
				overlay.addEventListener("click", (e) => {
					// Only close if clicking the overlay itself, not content
					if (e.target === overlay) {
						console.log("Overlay clicked, closing popup");
						this.hidePopup();
					}
				});
			}
		}
	}

	/**
	 * Setup keyboard listener (ESC key)
	 */
	setupKeyboardListener() {
		this.keyListener = (e) => {
			if (e.key === "Escape" && this.isVisible) {
				console.log("ESC key pressed, closing popup");
				this.hidePopup();
			}
		};

		document.addEventListener("keydown", this.keyListener);
	}

	/**
	 * Show popup with animation
	 */
	showPopup() {
		if (this.isVisible || this.isDismissed) {
			console.log("Popup already visible or dismissed, skipping show");
			return;
		}

		console.log("Showing popup");

		// Debug: Log current CSS custom properties
		const computedStyle = getComputedStyle(this.popup);
		console.log("CSS Properties Applied:", {
			width: computedStyle.getPropertyValue("--popup-width"),
			background: computedStyle.getPropertyValue("--popup-background"),
			overlayOpacity: computedStyle.getPropertyValue("--overlay-opacity"),
			padding: computedStyle.getPropertyValue("--popup-padding"),
			borderRadius: computedStyle.getPropertyValue("--popup-border-radius"),
		});

		this.isVisible = true;
		this.popup.style.display = "flex";

		// Force reflow for animation
		this.popup.offsetHeight;

		// Add active class for animation
		this.popup.classList.add("is-active");

		// Lock body scroll if enabled
		if (this.settings.lockScrolling) {
			this.lockBodyScroll();
		}

		// Focus management for accessibility
		this.manageFocus();

		// Trigger custom event
		this.dispatchPopupEvent("popup-shown");
	}

	/**
	 * Hide popup with animation
	 */
	hidePopup() {
		if (!this.isVisible) {
			console.log("Popup already hidden, skipping hide");
			return;
		}

		console.log("Hiding popup");
		this.isVisible = false;
		this.popup.classList.remove("is-active");

		// Wait for animation to complete before hiding
		setTimeout(() => {
			this.popup.style.display = "none";
		}, 300);

		// Unlock body scroll
		this.unlockBodyScroll();

		// Restore focus
		this.restoreFocus();

		// Handle dismissal - only mark as dismissed when explicitly closed by user
		this.markAsDismissed();

		// Trigger custom event
		this.dispatchPopupEvent("popup-hidden");
	}

	/**
	 * Lock body scroll
	 */
	lockBodyScroll() {
		const scrollY = window.scrollY;
		document.body.style.position = "fixed";
		document.body.style.top = `-${scrollY}px`;
		document.body.style.width = "100%";
		document.body.classList.add("popup-open");

		// Store scroll position
		document.body.dataset.scrollY = scrollY;
	}

	/**
	 * Unlock body scroll
	 */
	unlockBodyScroll() {
		const scrollY = document.body.dataset.scrollY || "0";
		document.body.style.position = "";
		document.body.style.top = "";
		document.body.style.width = "";
		document.body.classList.remove("popup-open");

		// Restore scroll position
		window.scrollTo(0, parseInt(scrollY, 10));
		delete document.body.dataset.scrollY;
	}

	/**
	 * Focus management for accessibility
	 */
	manageFocus() {
		// Store currently focused element
		this.previousFocus = document.activeElement;

		// Focus the popup or first focusable element within it
		const focusableElements = this.popup.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);

		if (focusableElements.length > 0) {
			focusableElements[0].focus();
		} else {
			this.popup.focus();
		}
	}

	/**
	 * Restore focus to previous element
	 */
	restoreFocus() {
		if (this.previousFocus && typeof this.previousFocus.focus === "function") {
			this.previousFocus.focus();
		}
	}

	/**
	 * Check dismissal status from localStorage
	 */
	checkDismissalStatus() {
		// If dismissal is disabled, always return false (not dismissed)
		if (!this.settings.dismiss || !this.settings.id) {
			console.log("Dismissal disabled or no ID, popup will show every time");
			return false;
		}

		try {
			const storageKey = "mr-popup-dismiss";
			const dismissedData = localStorage.getItem(storageKey);

			if (!dismissedData) return false;

			const dismissed = JSON.parse(dismissedData);
			const now = Date.now();

			// Clean expired entries
			const validEntries = dismissed.filter((entry) => entry.expiry > now);

			// Update storage with cleaned entries
			if (validEntries.length !== dismissed.length) {
				localStorage.setItem(storageKey, JSON.stringify(validEntries));
			}

			// Check if this popup is dismissed
			const isDismissed = validEntries.some(
				(entry) => entry.modalID === this.settings.id,
			);
			console.log("Dismissal check result:", isDismissed);
			return isDismissed;
		} catch (error) {
			console.warn("MR Blocks Popup: Could not access localStorage", error);
			return false;
		}
	}

	/**
	 * Mark popup as dismissed
	 */
	markAsDismissed() {
		// Only mark as dismissed if dismissal is enabled
		if (!this.settings.dismiss || !this.settings.id) {
			console.log("Dismissal disabled, not marking as dismissed");
			return;
		}

		try {
			const storageKey = "mr-popup-dismiss";
			const expiryTime =
				Date.now() + this.settings.dismissTime * 24 * 60 * 60 * 1000;

			let dismissed = [];
			const existingData = localStorage.getItem(storageKey);

			if (existingData) {
				dismissed = JSON.parse(existingData);
			}

			// Remove existing entry for this popup
			dismissed = dismissed.filter(
				(entry) => entry.modalID !== this.settings.id,
			);

			// Add new dismissal entry
			dismissed.push({
				modalID: this.settings.id,
				expiry: expiryTime,
			});

			localStorage.setItem(storageKey, JSON.stringify(dismissed));
			this.isDismissed = true;
			console.log(
				"Popup marked as dismissed for",
				this.settings.dismissTime,
				"days",
			);
		} catch (error) {
			console.warn("MR Blocks Popup: Could not save dismissal state", error);
		}
	}

	/**
	 * Dispatch custom popup events
	 */
	dispatchPopupEvent(eventName) {
		const event = new CustomEvent(eventName, {
			detail: {
				popup: this.popup,
				settings: this.settings,
			},
		});

		document.dispatchEvent(event);
	}

	/**
	 * Cleanup method for removing event listeners
	 */
	destroy() {
		// Remove event listeners
		if (this.scrollListener) {
			window.removeEventListener("scroll", this.scrollListener);
		}

		if (this.exitIntentListener) {
			document.removeEventListener("mouseleave", this.exitIntentListener);
		}

		if (this.keyListener) {
			document.removeEventListener("keydown", this.keyListener);
		}

		// Restore scroll if popup was open
		if (this.isVisible) {
			this.unlockBodyScroll();
		}
	}
}

/**
 * Initialize all popups when DOM is ready
 */
function initializePopups() {
	const popups = document.querySelectorAll(".wp-block-mr-blocks-popup");
	console.log(`MR Blocks: Found ${popups.length} popup(s) to initialize`);

	popups.forEach((popup) => {
		// Avoid double initialization
		if (!popup.dataset.mrPopupInitialized) {
			new PopupController(popup);
			popup.dataset.mrPopupInitialized = "true";
		}
	});
}

/**
 * Handle responsive behavior on window resize
 */
function handleResize() {
	const popups = document.querySelectorAll(".wp-block-mr-blocks-popup");

	popups.forEach((popup) => {
		const controller = popup.popupController;
		if (controller && controller.isDisabledOnCurrentDevice()) {
			controller.hidePopup();
		}
	});
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initializePopups);
} else {
	initializePopups();
}

// Handle resize events
window.addEventListener("resize", handleResize);

// Expose for debugging
window.MRPopupController = PopupController;
